import React from 'react';
import {TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {PRACTICE_VERDICT_STYLE} from '../../constants/practiceVerdicts';
import {practiceGoalLabel} from '../../utils/studyHelpers';
import {PracticeGoal, PracticeVerdictStatus} from '../../types';

export type PracticeOutcome = {
  status: 'ongoing' | 'won' | 'drawn' | 'lost';
  reason?: string;
};

export type PracticeVerdict = {
  status: PracticeVerdictStatus;
  message?: string | null;
};

type PracticeGoalBannerProps = {
  practiceOutcome: PracticeOutcome;
  practiceGoal: PracticeGoal | null | undefined;
  practiceMoveCount: number;
  /** Shown as a pill so the student knows the reply is coming. */
  isEngineThinking?: boolean;
  /** Opponent strength label, e.g. "Easy". */
  difficultyLabel?: string;
  takebackCount?: number;
};

export function PracticeGoalBanner({
  practiceOutcome,
  practiceGoal,
  practiceMoveCount,
  isEngineThinking = false,
  difficultyLabel,
  takebackCount = 0,
}: PracticeGoalBannerProps) {
  if (practiceOutcome.status !== 'ongoing') {
    return null;
  }
  const label = practiceGoalLabel(practiceGoal);
  const pillStyle = {
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: CCColors.LightYellow,
    alignSelf: 'flex-start' as const,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 6,
  };
  return (
    <Box style={{flexDirection: 'row', flexWrap: 'wrap', gap: 8}}>
      {label && (
        <Box style={pillStyle}>
          <Ionicons
            name="flag-outline"
            size={13}
            color={CCColors.Workbook.Brown}
          />
          <CCText
            fontSize={12}
            color={CCColors.Workbook.Brown}
            fontWeight="semibold">
            {label}
          </CCText>
        </Box>
      )}
      <Box style={pillStyle}>
        <Ionicons
          name="footsteps-outline"
          size={13}
          color={CCColors.Workbook.Brown}
        />
        <CCText
          fontSize={12}
          color={CCColors.Workbook.Brown}
          fontWeight="semibold">
          Move {practiceMoveCount}
        </CCText>
      </Box>
      {!!difficultyLabel && (
        <Box style={pillStyle}>
          <Ionicons
            name="hardware-chip-outline"
            size={13}
            color={CCColors.Workbook.Brown}
          />
          <CCText
            fontSize={12}
            color={CCColors.Workbook.Brown}
            fontWeight="semibold">
            {difficultyLabel}
          </CCText>
        </Box>
      )}
      {takebackCount > 0 && (
        <Box style={pillStyle}>
          <Ionicons
            name="arrow-undo-outline"
            size={13}
            color={CCColors.Workbook.Brown}
          />
          <CCText
            fontSize={12}
            color={CCColors.Workbook.Brown}
            fontWeight="semibold">
            {takebackCount} takeback{takebackCount > 1 ? 's' : ''}
          </CCText>
        </Box>
      )}
      {isEngineThinking && (
        <Box style={pillStyle}>
          <Ionicons
            name="ellipsis-horizontal"
            size={13}
            color={CCColors.Workbook.Brown}
          />
          <CCText
            fontSize={12}
            color={CCColors.Workbook.Brown}
            fontWeight="semibold">
            Computer is thinking…
          </CCText>
        </Box>
      )}
    </Box>
  );
}

type PracticeEndCardProps = {
  practiceOutcome: PracticeOutcome;
  onRetry: () => void;
  onAnalyseLine: () => void;
  hasNextChapter: boolean;
  onNextChapter: () => void;
};

// Matches lila's studyPracticeCtrl end state (win/fail/draw), replacing
// the old "reached end of line" card whenever an explicit practice_goal
// is set - takes priority over the per-move verdict since the game/goal
// is now decided. Retry / Next chapter mirror the lesson-mode end card;
// "Analyse this line" hands the played game off to free analysis,
// matching Lichess linking to analysis of the practiced line.
function PracticeEndCard({
  practiceOutcome,
  onRetry,
  onAnalyseLine,
  hasNextChapter,
  onNextChapter,
}: PracticeEndCardProps) {
  if (practiceOutcome.status === 'ongoing') {
    return null;
  }
  const isWin = practiceOutcome.status === 'won';
  const isDraw = practiceOutcome.status === 'drawn';
  const iconName = isWin
    ? 'trophy-outline'
    : isDraw
    ? 'remove-circle-outline'
    : 'close-circle-outline';
  const color = isWin
    ? CCColors.Green
    : isDraw
    ? CCColors.Workbook.Brown
    : CCColors.Error;
  const bg = isWin
    ? CCColors.Workbook.AquaTint
    : isDraw
    ? CCColors.LightYellow
    : CCColors.Workbook.RedTint;
  const title = isWin ? 'Success!' : isDraw ? 'Draw' : 'Not this time';

  return (
    <Box>
      <Box
        style={{
          marginTop: 12,
          padding: 14,
          borderRadius: 8,
          backgroundColor: bg,
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 10,
          borderWidth: 1,
          borderColor: color + '33',
        }}>
        <Ionicons
          name={iconName}
          size={22}
          color={color}
          style={{marginTop: 2}}
        />
        <Box style={{flex: 1}}>
          <CCText fontWeight="bold" fontSize={14} color={color}>
            {title}
          </CCText>
          <Spacer spacing={4} />
          <CCText fontSize={12} color={color} style={{lineHeight: 18}}>
            {practiceOutcome.reason}
          </CCText>
        </Box>
      </Box>
      <Box style={{flexDirection: 'row', gap: 10, marginTop: 8}}>
        <TouchableOpacity
          onPress={onRetry}
          style={{
            paddingVertical: 8,
            paddingHorizontal: 14,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: CCColors.Workbook.Brown,
          }}>
          <CCText
            fontSize={12}
            fontWeight="bold"
            color={CCColors.Workbook.Brown}>
            Retry
          </CCText>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onAnalyseLine}
          style={{
            paddingVertical: 8,
            paddingHorizontal: 14,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: CCColors.Workbook.Brown,
          }}>
          <CCText
            fontSize={12}
            fontWeight="bold"
            color={CCColors.Workbook.Brown}>
            Analyse this line
          </CCText>
        </TouchableOpacity>
        {hasNextChapter && (
          <TouchableOpacity
            onPress={onNextChapter}
            style={{
              paddingVertical: 8,
              paddingHorizontal: 14,
              borderRadius: 6,
              backgroundColor: CCColors.Green,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
            }}>
            <CCText fontSize={12} fontWeight="bold" color={CCColors.White}>
              Next Chapter
            </CCText>
            <Ionicons name="arrow-forward" size={14} color={CCColors.White} />
          </TouchableOpacity>
        )}
      </Box>
    </Box>
  );
}

type PracticeFeedbackCardProps = {
  practiceOutcome: PracticeOutcome;
  practiceVerdict: PracticeVerdict;
  practiceGoal: PracticeGoal | null | undefined;
  dismissedEndCard: boolean;
  // True when the student is on a real move with no authored continuation —
  // the "reached end of the authored line" fallback when no goal is set.
  reachedEndOfLine: boolean;
  onRetry: () => void;
  onAnalyseLine: () => void;
  hasNextChapter: boolean;
  onNextChapter: () => void;
};

export function PracticeFeedbackCard({
  practiceOutcome,
  practiceVerdict,
  practiceGoal,
  dismissedEndCard,
  reachedEndOfLine,
  onRetry,
  onAnalyseLine,
  hasNextChapter,
  onNextChapter,
}: PracticeFeedbackCardProps) {
  if (practiceOutcome.status !== 'ongoing' && !dismissedEndCard) {
    return (
      <PracticeEndCard
        practiceOutcome={practiceOutcome}
        onRetry={onRetry}
        onAnalyseLine={onAnalyseLine}
        hasNextChapter={hasNextChapter}
        onNextChapter={onNextChapter}
      />
    );
  }

  if (!practiceGoal?.type && reachedEndOfLine) {
    // No explicit goal authored - fall back to the original "reached end
    // of the authored line" signal.
    return (
      <Box
        style={{
          marginTop: 12,
          padding: 14,
          borderRadius: 8,
          backgroundColor: CCColors.Workbook.AquaTint,
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 10,
          borderWidth: 1,
          borderColor: updateColorWithOpacity(CCColors.Green, 0.2),
        }}>
        <Ionicons
          name="trophy-outline"
          size={22}
          color={CCColors.Workbook.GoldText}
          style={{marginTop: 2}}
        />
        <Box style={{flex: 1}}>
          <CCText
            fontWeight="bold"
            fontSize={14}
            color={CCColors.Workbook.GoldText}>
            Solved!
          </CCText>
          <Spacer spacing={4} />
          <CCText
            fontSize={12}
            color={CCColors.Workbook.GoldText}
            style={{lineHeight: 18}}>
            You've reached the end of the line. Well played!
          </CCText>
        </Box>
      </Box>
    );
  }

  if (practiceVerdict.status === 'idle' || !practiceVerdict.message) {
    return (
      <Box
        style={{
          marginTop: 12,
          padding: 14,
          borderRadius: 8,
          backgroundColor: CCColors.LightYellow,
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 10,
          borderWidth: 1,
          borderColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.2),
        }}>
        <Ionicons
          name="information-circle-outline"
          size={22}
          color={CCColors.Workbook.Brown}
          style={{marginTop: 2}}
        />
        <Box style={{flex: 1}}>
          <CCText
            fontWeight="bold"
            fontSize={14}
            color={CCColors.Workbook.Brown}>
            Practice with Computer
          </CCText>
          <Spacer spacing={4} />
          <CCText
            fontSize={12}
            color={CCColors.Workbook.Brown}
            style={{lineHeight: 18}}>
            Play a move — the engine will reply and coach you on each move you
            make.
          </CCText>
        </Box>
      </Box>
    );
  }

  const style =
    PRACTICE_VERDICT_STYLE[practiceVerdict.status] ||
    PRACTICE_VERDICT_STYLE.good;
  return (
    <Box
      style={{
        marginTop: 12,
        padding: 14,
        borderRadius: 8,
        backgroundColor: style.bg,
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        borderWidth: 1,
        borderColor: style.color + '33',
      }}>
      <Ionicons
        name={style.icon}
        size={22}
        color={style.color}
        style={{marginTop: 2}}
      />
      <Box style={{flex: 1}}>
        <CCText fontWeight="bold" fontSize={14} color={style.color}>
          {style.title}
        </CCText>
        <Spacer spacing={4} />
        <CCText fontSize={12} color={style.color} style={{lineHeight: 18}}>
          {practiceVerdict.message}
        </CCText>
      </Box>
    </Box>
  );
}
